// src/web-app/lib/auth/firebase.js
// Reuse Firebase auth from desktop-app - import and re-export
import { initializeApp, getApps } from "firebase/app";
import {
  getAuth,
  signInWithPopup,
  signInWithRedirect,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  getRedirectResult as firebaseGetRedirectResult,
  GoogleAuthProvider,
  OAuthProvider,
  onAuthStateChanged,
  signOut,
  signInWithEmailLink,
  isSignInWithEmailLink,
  sendSignInLinkToEmail,
} from "firebase/auth";
import { getStorage, ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { firebaseConfig } from "../config.js";

const app = getApps().length ? getApps()[0] : initializeApp(firebaseConfig);
const auth = getAuth(app);
const storage = getStorage(app);

export function getAuthInstance() {
  return getAuth(app);
}

export function onUser(cb) {
  return onAuthStateChanged(auth, cb);
}

export async function signInWithGoogle() {
  const prov = new GoogleAuthProvider();
  await signInWithRedirect(auth, prov);
}

export async function signInWithMicrosoft() {
  const prov = new OAuthProvider("microsoft.com");
  await signInWithRedirect(auth, prov);
}

export async function getRedirectResult() {
  try {
    const result = await firebaseGetRedirectResult(auth);
    return result;
  } catch (error) {
    console.error('Redirect result error:', error);
    return null;
  }
}

export async function signInWithEmail(email, password) {
  try {
    const result = await signInWithEmailAndPassword(auth, email, password);
    return result.user;
  } catch (error) {
    console.error('Email sign-in error:', error);
    throw error;
  }
}

export async function createAccount(email, password) {
  try {
    const result = await createUserWithEmailAndPassword(auth, email, password);
    return result.user;
  } catch (error) {
    console.error('Account creation error:', error);
    throw error;
  }
}

export async function sendSignInLink(email) {
  const actionCodeSettings = { url: window.location.href, handleCodeInApp: true };
  window.localStorage.setItem("pendingEmail", email);
  await sendSignInLinkToEmail(auth, email, actionCodeSettings);
}

export async function completeEmailLinkSignIn() {
  if (isSignInWithEmailLink(auth, window.location.href)) {
    const email = window.localStorage.getItem("pendingEmail");
    if (!email) return null;
    const cred = await signInWithEmailLink(auth, email, window.location.href);
    window.localStorage.removeItem("pendingEmail");
    return cred.user;
  }
  return auth.currentUser || null;
}

export async function getFirebaseIdToken(forceRefresh = false) {
  const u = auth.currentUser;
  if (!u) throw new Error("No Firebase user");
  return await u.getIdToken(forceRefresh);
}

/**
 * Decoded custom claims of the signed-in user (empty object when signed out
 * or on error). Mirrors desktop-app/lib/auth/firebase.js.
 */
export async function getFirebaseClaims(forceRefresh = false) {
  const u = auth.currentUser;
  if (!u) return {};
  try {
    const res = await u.getIdTokenResult(forceRefresh);
    return res?.claims || {};
  } catch (error) {
    console.warn("[AUTH] Failed to read ID token claims:", error);
    return {};
  }
}

/**
 * Whether the current user is EU-coded / bound to Zero Data Retention
 * (Gemini-only models). Keep the claim logic identical to
 * desktop-app/lib/auth/firebase.js — the server derives the same flag at
 * /session/bind and enforces it in the LLM gateway.
 */
export async function isZdrRestrictedUser(forceRefresh = false) {
  const claims = await getFirebaseClaims(forceRefresh);
  const residency = String(claims.data_residency ?? claims.region ?? "").toLowerCase();
  return (
    residency === "eu" ||
    claims.zdr === true ||
    claims.eu === true ||
    claims.gemini_only === true
  );
}

export async function logoutFirebase() {
  await signOut(auth);
}

/**
 * Upload an HTML report to Firebase Storage and return a public URL
 * @param {Blob} htmlBlob - The HTML file as a Blob
 * @param {string} filename - The filename for the report (e.g., "document_citation_report.html")
 * @param {string} userId - The current user's ID
 * @param {string} documentId - Optional document ID for organization
 * @param {string} contentType - MIME type of the report (default text/html; pass application/pdf for annotated PDF reports)
 * @returns {Promise<string>} Public URL to access the uploaded report
 */
export async function uploadHtmlReport(htmlBlob, filename, userId, documentId = null, contentType = 'text/html') {
  try {
    // Wait for auth state to be ready (similar to bootstrap)
    let currentUser = auth.currentUser;
    if (!currentUser) {
      // Wait up to 3 seconds for auth state to initialize
      currentUser = await new Promise((resolve) => {
        if (auth.currentUser) {
          resolve(auth.currentUser);
          return;
        }
        
        const timeoutId = setTimeout(() => {
          unsubscribe();
          resolve(null);
        }, 3000);
        
        const unsubscribe = onAuthStateChanged(auth, (user) => {
          clearTimeout(timeoutId);
          unsubscribe();
          resolve(user);
        });
      });
    }
    
    if (!currentUser) {
      throw new Error('User not authenticated. Please sign in.');
    }
    
    // Verify userId matches the authenticated user's uid
    if (currentUser.uid !== userId) {
      throw new Error(`User ID mismatch: authenticated as ${currentUser.uid}, but trying to upload as ${userId}`);
    }
    
    // Ensure we have a fresh auth token before uploading
    // This ensures Firebase Storage receives the auth token
    const token = await currentUser.getIdToken(true);
    console.log('[STORAGE] Auth token obtained, length:', token.length);
    
    // Create a storage path: reports/{userId}/{documentId or 'default'}/{filename}
    const pathSegments = ['reports', userId];
    if (documentId) {
      pathSegments.push(documentId);
    }
    pathSegments.push(filename);
    const storagePath = pathSegments.join('/');
    
    // Create a reference to the file
    const storageRef = ref(storage, storagePath);
    
    // Upload the blob
    console.log('[STORAGE] Uploading report to:', storagePath);
    console.log('[STORAGE] Authenticated user:', currentUser.uid, currentUser.email);
    const snapshot = await uploadBytes(storageRef, htmlBlob, {
      contentType: contentType,
      cacheControl: 'public, max-age=31536000' // Cache for 1 year
    });
    
    // Get the public download URL
    const downloadURL = await getDownloadURL(snapshot.ref);
    console.log('[STORAGE] Report uploaded successfully:', downloadURL);
    
    return downloadURL;
  } catch (error) {
    console.error('[STORAGE] Failed to upload report:', error);
    throw new Error(`Failed to upload report: ${error.message}`);
  }
}

